import React, { useState } from 'react';
import {
  Container, Row, Col, Card, Badge, Button, Spinner, Alert, Dropdown,
  Modal, Form
} from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import {
  getToolDetail,
  getToolAssignmentHistory,
  retireTool,
  updateToolCondition,
  reportToolLost
} from '../../services/toolAssignmentService';
import ToolEditModal from './components/ToolEditModal';
import ToolAssignModal from './components/ToolAssignModal';
import ToolReturnModal from './components/ToolReturnModal';
import DestructiveConfirmModal from '../../components/common/DestructiveConfirmModal';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './ToolList.css';

const STATUS_LABELS = {
  available: 'Disponible',
  assigned: 'Asignada',
  maintenance: 'Mantenimiento',
  retired: 'Retirada'
};
const STATUS_COLORS = {
  available: 'success',
  assigned: 'primary',
  maintenance: 'warning',
  retired: 'secondary'
};
const CONDITION_LABELS = {
  good: 'Buena',
  damaged: 'Dañada',
  lost: 'Perdida',
  maintenance: 'Mantenimiento'
};
const CONDITION_COLORS = {
  good: 'success',
  damaged: 'warning',
  lost: 'danger',
  maintenance: 'info'
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('es-CR') : '—';
const daysBetween = (a, b = new Date()) => {
  if (!a) return 0;
  return Math.floor((new Date(b).getTime() - new Date(a).getTime()) / (1000 * 60 * 60 * 24));
};

const ToolDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const [editOpen, setEditOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [returnState, setReturnState] = useState(null);
  const [conditionOpen, setConditionOpen] = useState(false);
  const [newCondition, setNewCondition] = useState('');
  const [conditionNotes, setConditionNotes] = useState('');
  const [retireOpen, setRetireOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['tool-detail', id],
    queryFn: () => getToolDetail(id),
    enabled: !!id
  });

  const { data: historyData, isLoading: loadingHistory } = useQuery({
    queryKey: ['tool-history', id],
    queryFn: () => getToolAssignmentHistory(id, { limit: 100 }),
    enabled: !!id
  });

  const retireMutation = useMutation({
    mutationFn: ({ reason }) => retireTool(id, { reason }),
    onSuccess: ({ error: err }) => {
      if (err) { toast.error(err.message); return; }
      toast.success('Herramienta retirada.');
      queryClient.invalidateQueries({ queryKey: ['tool-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      setRetireOpen(false);
    },
    onError: (e) => toast.error(e.message)
  });

  const conditionMutation = useMutation({
    // 'lost' no es una condición más: además de la condición hay que cerrar la
    // asignación abierta y avisar a los admins. Ese camino vive completo en
    // `reportToolLost` (ver el comentario de esa función).
    mutationFn: () => (newCondition === 'lost'
      ? reportToolLost({ tool_id: id, reason: conditionNotes || null })
      : updateToolCondition({
        tool_id: id,
        condition: newCondition,
        notes: conditionNotes || null
      })),
    onSuccess: ({ error: err }) => {
      if (err) { toast.error(err.message); return; }
      toast.success('Condición actualizada.');
      queryClient.invalidateQueries({ queryKey: ['tool-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      setConditionOpen(false);
      setNewCondition('');
      setConditionNotes('');
    },
    onError: (e) => toast.error(e.message)
  });

  if (!isAdmin) {
    return <Alert variant="danger" className="m-3">Acceso restringido a administradores.</Alert>;
  }

  if (isLoading) {
    return (
      <Container fluid className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error || !data?.data) {
    return (
      <Container fluid className="py-3">
        <Alert variant="danger">
          {error?.message || 'Herramienta no encontrada.'}{' '}
          <Link to="/herramientas">Volver al listado</Link>
        </Alert>
      </Container>
    );
  }

  const tool = data.data;
  const activeAssignment = tool._active_assignment;
  const history = historyData?.data || [];

  const openCondition = () => {
    setNewCondition(tool.condition || 'good');
    setConditionNotes('');
    setConditionOpen(true);
  };

  const handleRetire = () => setRetireOpen(true);

  return (
    <DashboardLayout>
    <Container fluid className="py-3">
      <Button
        variant="link"
        className="p-0 mb-2 text-muted small"
        onClick={() => navigate('/herramientas')}
      >
        <i className="bi bi-arrow-left me-1" />Herramientas
      </Button>

      <Row className="g-3 mb-4">
        <Col xs={12} md={4} lg={3}>
          <Card className="h-100">
            <div className="tool-card-photo-wrap" style={{ paddingTop: '100%' }}>
              {tool.image_url ? (
                <img src={tool.image_url} alt={tool.name} className="tool-card-photo" />
              ) : (
                <div className="tool-card-photo-placeholder">
                  <i className="bi bi-tools" />
                </div>
              )}
            </div>
          </Card>
        </Col>

        <Col xs={12} md={8} lg={9}>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <h2 className="modern-title mb-1">{tool.name}</h2>
              <div className="text-muted font-monospace small">{tool.serial_number}</div>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <Badge bg={STATUS_COLORS[tool.status]} className="fs-6">
                {STATUS_LABELS[tool.status]}
              </Badge>
              <Dropdown align="end">
                <Dropdown.Toggle variant="outline-secondary" size="sm" aria-label={`Acciones de ${tool.name}`}>
                  <i className="bi bi-three-dots-vertical" />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {tool.status !== 'retired' && tool.status !== 'assigned' && (
                    <Dropdown.Item onClick={() => setAssignOpen(true)}>
                      <i className="bi bi-person-check me-2 text-primary" />Asignar
                    </Dropdown.Item>
                  )}
                  {activeAssignment && (
                    <Dropdown.Item
                      onClick={() => setReturnState({
                        assignment: {
                          ...activeAssignment, tool_id: tool.id,
                          _toolName: tool.name, _toolSerial: tool.serial_number
                        },
                        mode: 'return'
                      })}
                    >
                      <i className="bi bi-box-arrow-in-left me-2 text-success" />
                      Forzar devolución
                    </Dropdown.Item>
                  )}
                  <Dropdown.Item onClick={() => setEditOpen(true)}>
                    <i className="bi bi-pencil me-2" />Editar metadata
                  </Dropdown.Item>
                  <Dropdown.Item onClick={openCondition}>
                    <i className="bi bi-clipboard2-pulse me-2" />Cambiar condición
                  </Dropdown.Item>
                  {activeAssignment && (
                    <Dropdown.Item
                      className="text-danger"
                      onClick={() => setReturnState({
                        assignment: {
                          ...activeAssignment, tool_id: tool.id,
                          _toolName: tool.name, _toolSerial: tool.serial_number
                        },
                        mode: 'lost'
                      })}
                    >
                      <i className="bi bi-exclamation-triangle me-2" />Marcar perdida
                    </Dropdown.Item>
                  )}
                  {tool.status !== 'retired' && (
                    <>
                      <Dropdown.Divider />
                      <Dropdown.Item className="text-danger" onClick={handleRetire}>
                        <i className="bi bi-archive me-2" />Retirar
                      </Dropdown.Item>
                    </>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          <Row className="g-2 mt-2">
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Categoría</small>
              <strong>{tool._categoryLabel || '—'}</strong>
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Marca</small>
              <strong>{tool.brand || '—'}</strong>
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Modelo</small>
              <strong>{tool.model || '—'}</strong>
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Condición</small>
              <Badge bg={CONDITION_COLORS[tool.condition]}>
                {CONDITION_LABELS[tool.condition]}
              </Badge>
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Ubicación</small>
              {/* `_locationLabel` resuelve `location_rel` (lo que escribe el
                  modal de edición) y sólo cae a la columna de texto legada
                  `location` si no hay ubicación asignada. Pintar `location`
                  a secas mostraba la ubicación VIEJA después de editarla. */}
              <strong>{tool._locationLabel || '—'}</strong>
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Registrada</small>
              <strong>{fmtDate(tool.created_at)}</strong>
            </Col>
          </Row>

          {tool.description && (
            <div className="mt-3">
              <small className="text-muted d-block">Descripción</small>
              <p className="mb-0">{tool.description}</p>
            </div>
          )}
          {tool.notes && (
            <div className="mt-3">
              <small className="text-muted d-block">Notas internas</small>
              <p className="mb-0 text-muted">{tool.notes}</p>
            </div>
          )}
        </Col>
      </Row>

      {activeAssignment && (
        <Card className="mb-4 border-primary">
          <Card.Header className="bg-primary bg-opacity-10 border-primary">
            <strong><i className="bi bi-person-fill me-2" />Asignación activa</strong>
          </Card.Header>
          <Card.Body>
            <Row className="align-items-center">
              <Col xs={12} md={4}>
                <small className="text-muted d-block">Holder</small>
                <strong>{activeAssignment.holder?.full_name || '—'}</strong>
              </Col>
              <Col xs={12} md={4}>
                <small className="text-muted d-block">Proyecto</small>
                {activeAssignment.project ? (
                  <Link to={`/proyectos/${activeAssignment.project.id}`}>
                    {activeAssignment.project.project_number
                      ? `${activeAssignment.project.project_number} — `
                      : ''}
                    {activeAssignment.project.title}
                  </Link>
                ) : (
                  <span className="text-muted">Sin proyecto</span>
                )}
              </Col>
              <Col xs={12} md={2}>
                <small className="text-muted d-block">Desde</small>
                <strong>{fmtDate(activeAssignment.assigned_at)}</strong>
              </Col>
              <Col xs={12} md={2}>
                <small className="text-muted d-block">Días fuera</small>
                {(() => {
                  const days = daysBetween(activeAssignment.assigned_at);
                  return (
                    <strong className={days > 30 ? 'text-warning' : ''}>{days}d</strong>
                  );
                })()}
              </Col>
              {activeAssignment.notes && (
                <Col xs={12} className="mt-3">
                  <small className="text-muted d-block">Notas al asignar</small>
                  <p className="mb-0">{activeAssignment.notes}</p>
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong><i className="bi bi-clock-history me-2" />Historial de asignaciones</strong>
          <small className="text-muted">{history.length} registros</small>
        </Card.Header>
        <Card.Body className="p-0">
          {loadingHistory && (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          )}
          {!loadingHistory && history.length === 0 && (
            <Alert variant="info" className="m-3 mb-0">
              Sin historial. La herramienta nunca ha sido asignada.
            </Alert>
          )}
          {!loadingHistory && history.length > 0 && (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>Holder</th>
                    <th>Proyecto</th>
                    <th>Asignada por</th>
                    <th>Asignada</th>
                    <th>Devuelta</th>
                    <th>Duración</th>
                    <th>Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => {
                    const days = h.returned_at
                      ? daysBetween(h.assigned_at, h.returned_at)
                      : daysBetween(h.assigned_at);
                    return (
                      <tr key={h.id}>
                        <td>
                          {h.holder?.full_name || '—'}
                          {h.parent_assignment_id && (
                            <Badge bg="info" className="ms-2" title="Sub-asignación">
                              sub
                            </Badge>
                          )}
                        </td>
                        <td>
                          {h.project
                            ? (h.project.project_number || h.project.title)
                            : <span className="text-muted">—</span>}
                        </td>
                        <td>{h.assigner?.full_name || '—'}</td>
                        <td>{fmtDate(h.assigned_at)}</td>
                        <td>
                          {h.returned_at
                            ? fmtDate(h.returned_at)
                            : <Badge bg="primary">Activa</Badge>}
                        </td>
                        <td>{days}d</td>
                        <td>
                          <small className="text-muted">{h.notes || '—'}</small>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      <ToolEditModal show={editOpen} onHide={() => setEditOpen(false)} tool={tool} />
      <ToolAssignModal show={assignOpen} onHide={() => setAssignOpen(false)} tool={tool} />
      <ToolReturnModal
        show={!!returnState}
        onHide={() => setReturnState(null)}
        assignment={returnState?.assignment}
        mode={returnState?.mode}
      />

      {conditionOpen && (
        <ConditionModal
          tool={tool}
          condition={newCondition}
          setCondition={setNewCondition}
          notes={conditionNotes}
          setNotes={setConditionNotes}
          isPending={conditionMutation.isPending}
          onConfirm={() => conditionMutation.mutate()}
          onClose={() => setConditionOpen(false)}
        />
      )}

      <DestructiveConfirmModal
        show={retireOpen}
        onHide={() => setRetireOpen(false)}
        title="Retirar herramienta del inventario"
        icon="bi-archive"
        variant="danger"
        description={
          <>
            Vas a retirar <strong>{tool.name}</strong>
            {tool.serial_number && <> <code className="ms-1">({tool.serial_number})</code></>} del catálogo activo.
          </>
        }
        consequences={[
          'La herramienta pasa a estado "retirada".',
          'No aparecerá en filtros de bodega ni en pickup.',
          'Las asignaciones históricas y el audit log se conservan.',
          'Para reactivarla, un admin debe cambiarle el status manualmente.'
        ]}
        confirmText={tool.serial_number}
        confirmLabel="serial de la herramienta"
        reasonRequired
        reasonLabel="Motivo del retiro"
        reasonPlaceholder="Fin de vida útil, daño irreparable, robo, reemplazo..."
        submitLabel="Confirmar retiro"
        onConfirm={({ reason }) => retireMutation.mutate({ reason })}
        pending={retireMutation.isPending}
      />
    </Container>
    </DashboardLayout>
  );
};

const ConditionModal = ({ tool, condition, setCondition, notes, setNotes, isPending, onConfirm, onClose }) => {
  return (
    <Modal show={true} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-clipboard2-pulse me-2" />Cambiar condición
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3"><strong>{tool.name}</strong></p>
        <Form.Group className="mb-3">
          <Form.Label>Nueva condición</Form.Label>
          <Form.Select value={condition} onChange={e => setCondition(e.target.value)} disabled={isPending}>
            {Object.entries(CONDITION_LABELS).map(([s, l]) => (
              <option key={s} value={s}>{l}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Notas</Form.Label>
          <Form.Control
            as="textarea" rows={2} value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Detalles del cambio de condición..."
            disabled={isPending}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isPending}>Cancelar</Button>
        <Button variant="primary" onClick={onConfirm} disabled={isPending}>
          {isPending && <Spinner size="sm" animation="border" className="me-1" />}
          Guardar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ToolDetail;
